import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { CSM, EDIT_CUSTOMER as EDIT, INFO, QUERY_KEYS, ROLES } from '../../../constants';
import { getAllUsers, popup, updateAccount } from '../../../utils';
import { CircleSpinner, Edit } from '../../../assets/svg';
import useWindowSize from '../../../hooks/useWindowSize';
import SSCSelect from '../../shared/SSCSelect';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const ManageCSMBox = ({ data }) => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [selectData, setSelectData] = useState(null);
    const [edit, setEdit] = useState(false);
    const { width } = useWindowSize();
    const isSmallScreen = width < 720;
    const role = useSelector((s) => s.user.role);

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const {
        data: csmList,
        isError,
        error,
        isFetching,
    } = useQuery({
        queryKey: [QUERY_KEYS.CSM_USER, debouncedSearch, '25'],
        queryFn: async () =>
            await getAllUsers({
                limit: 25,
                offset: 1,
                search: debouncedSearch,
                filter: { status: true },
                role: [ROLES.CSM],
            }),
        select: (data) =>
            data?.data?.map((csm) => ({
                label: csm.full_name,
                value: csm,
            })),
        placeholderData: [],
    });

    if (isError) popup('error', error?.message);

    const updateAccountMutation = useMutation({
        mutationFn: async (data) => await updateAccount(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMERS], exact: false });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, data.account?.account_id],
            });
            popup('success', response.message);
            setEdit((prev) => !prev);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const handleSave = () => {
        if (data?.account?.account_id && selectData?.id) {
            const mutationData = {
                body: { csm_id: selectData?.id },
                id: data.account?.account_id,
            };
            updateAccountMutation.mutate(mutationData);
        }
    };

    const handleEdit = () => {
        setEdit(true);
    };

    const handleDiscard = () => {
        setEdit(false);
        setSelectData(null);
    };

    return (
        <>
            <div className='tabs-set h-25'>
                <div className='d-flex justify-content-end '>
                    <div>
                        {!edit && role !== ROLES.SALES_AGENT && (
                            <span title='Edit' id='action-btn' onClick={handleEdit}>
                                <Edit className='cursor-pointer' />
                            </span>
                        )}
                    </div>
                </div>
                {!data.account.csm_name && !edit && (
                    <div
                        className='mt-4 vh-50 d-flex justify-content-center'
                        style={{ fontSize: '16px' }}
                    >
                        {INFO.NO_CSM_FOUND}
                    </div>
                )}
                <div className='form-area mt-4'>
                    {edit ? (
                        <div className='d-flex flex-column gap-3 px-2'>
                            <div className={isSmallScreen ? 'w-100' : 'w-50'}>
                                <SSCSelect
                                    label={CSM.PAGE_NAME}
                                    placeholder={
                                        data.account.csm_name ? data.account.csm_name : 'Select CSM'
                                    }
                                    options={csmList}
                                    value={
                                        selectData
                                            ? { label: selectData?.full_name, value: selectData }
                                            : null
                                    }
                                    onChange={(d) => setSelectData(d?.value)}
                                    onInputChange={(v) => {
                                        setSearch(v);
                                    }}
                                    isOptionSelected={(option) =>
                                        selectData?.id  ? option.value?.id === selectData?.id :
                                        option.value?.id === data.account?.csm_id
                                    }
                                    isClearable
                                    isLoading={isFetching}
                                />
                            </div>

                            <div className='d-flex flex-wrap gap-2 align-items-center justify-content-end mb-1'>
                                <button
                                    onClick={handleDiscard}
                                    className={`form-white-button d-flex align-items-center justify-content-center border-0 cursor-pointer me-3`}
                                    disabled={updateAccountMutation.isPending}
                                >
                                    {EDIT.BUTTON_TEXT.CANCEL}
                                </button>
                                <button
                                    onClick={handleSave}
                                    className='form-button d-flex align-items-center justify-content-center border-0'
                                    disabled={
                                        updateAccountMutation.isPending ||
                                        !selectData ||
                                        selectData?.csm_id === data?.account?.csm_id
                                    }
                                >
                                    {updateAccountMutation.isPending && (
                                        <span className='me-1'>
                                            <CircleSpinner />
                                        </span>
                                    )}
                                    {EDIT.BUTTON_TEXT.SAVE}
                                </button>
                            </div>
                        </div>
                    ) : (
                        data.account.csm_name && (
                            <div className='d-flex  border-bottom justify-content-start ps-5 '>
                                <div className='d-flex ps-4 flex-column mb-3 input-set '>
                                    <label> {'CSM'}</label>
                                    <span>{data?.account?.csm_name}</span>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    );
};
ManageCSMBox.propTypes = {
    data: PropTypes.object
};
